/* Isolated disclosure navigation; native dialog supplies modal focus containment. */
if (!customElements.get('impact-navigation-2027')) {
  customElements.define('impact-navigation-2027', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.dialog = this.querySelector('dialog');
      this.desktop = [...this.querySelectorAll('[data-nav27-desktop]')];
      this.mobile = [...this.querySelectorAll('[data-nav27-mobile]')];
      this.breakpoint = matchMedia('(max-width: 991px)');
      this.opener = document.querySelector(`[data-nav27-open="${this.dialog.id}"]`);
      document.addEventListener('click', event => {
        const opener = event.target.closest('[data-nav27-open]');
        if (opener?.getAttribute('data-nav27-open') !== this.dialog.id) return;
        this.opener = opener;
        this.closeDesktop();
        this.dialog.showModal();
        this.opener.setAttribute('aria-expanded', 'true');
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }, { signal });
      this.querySelector('[data-nav27-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => {
        this.restore();
        this.suppressReturnFocus = false;
      }, { signal });
      this.addEventListener('click', event => {
        if (!event.target.closest('[data-nav27-help-action] a, [data-nav27-help-action] button')) return;
        if (this.dialog.open) {
          this.suppressReturnFocus = true;
          this.dialog.close();
          document.documentElement.style.overflow = this.previousOverflow ?? '';
          this.opener?.setAttribute('aria-expanded', 'false');
          this.opener?.focus();
        } else {
          const opened = this.desktop.find(details => details.open);
          this.closeDesktop();
          opened?.querySelector('summary').focus();
        }
      }, { capture: true, signal });
      this.querySelector('.nav27-scrim').addEventListener('click', () => this.closeDesktop(), { signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('a[href], button, summary, [tabindex="0"]')].filter(el => el.getClientRects().length && !el.disabled);
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, { signal });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, { signal });
      const sync = details => {
        const summary = details.querySelector(':scope > summary');
        summary.setAttribute('aria-expanded', String(details.open));
        if (details.matches('[data-nav27-desktop]')) summary.setAttribute('aria-controls', details.querySelector('.nav27-panel').id);
        this.toggleAttribute('data-panel-open', this.desktop.some(item => item.open));
        this.style.setProperty('--nav27-header-bottom', `${this.closest('header-component').getBoundingClientRect().bottom}px`);
      };
      this.querySelectorAll('details').forEach(details => {
        sync(details);
        details.addEventListener('toggle', () => {
          sync(details);
          if (!details.open) return;
          const siblings = details.matches('[data-nav27-desktop]') ? this.desktop : details.matches('[data-nav27-mobile]') ? this.mobile : [];
          siblings.forEach(other => { if (other !== details) other.open = false; });
          if (details.matches('[data-nav27-mobile]')) this.querySelector('.nav27-dialog__scroll').scrollTop = 0;
        }, { signal });
      });
      this.desktop.forEach(details => {
        const summary = details.querySelector(':scope > summary');
        let openedByHover = false;
        summary.addEventListener('click', event => {
          if (event.detail && openedByHover && details.open) event.preventDefault();
          openedByHover = false;
        }, { signal });
        details.addEventListener('pointerenter', event => {
          clearTimeout(this.hoverTimer);
          if (event.pointerType === 'mouse' && !this.breakpoint.matches) {
            this.closeDesktop(details);
            openedByHover = !details.open;
            details.open = true;
          }
        }, { signal });
        details.addEventListener('pointerleave', () => {
          this.hoverTimer = setTimeout(() => {
            if (!details.contains(document.activeElement)) details.open = false;
          }, 180);
        }, { signal });
        details.addEventListener('focusout', () => {
          requestAnimationFrame(() => {
            if (this.isConnected && !details.contains(document.activeElement)) details.open = false;
          });
        }, { signal });
      });
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.closeDesktop();
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.dialog.open) return;
        const opened = this.desktop.find(details => details.open);
        if (opened) {
          event.preventDefault();
          opened.open = false;
          opened.querySelector('summary').focus();
        }
      }, { signal });
      this.breakpoint.addEventListener('change', () => {
        this.closeDesktop();
        if (this.dialog.open) this.dialog.close();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        if (event.detail.blockId !== this.dataset.nav27BlockId) return;
        if (!this.breakpoint.matches && this.desktop[0]) this.desktop[0].open = true;
      }, { signal });
    }
    closeDesktop(except) {
      this.desktop?.forEach(details => { if (details !== except) details.open = false; });
    }
    restore() {
      document.documentElement.style.overflow = this.previousOverflow ?? '';
      this.opener?.setAttribute('aria-expanded', 'false');
      if (!this.suppressReturnFocus && this.opener?.isConnected && this.breakpoint.matches) this.opener.focus();
    }
    disconnectedCallback() {
      clearTimeout(this.hoverTimer);
      if (this.dialog?.open) { this.dialog.close(); this.restore(); }
      this.abort?.abort();
    }
  });
}
